/**
 * Natural Language Intent Detector
 * Uses fuzzy matching and pattern recognition to determine user intent
 */

import type { NLIntent, NLToken } from './ast.js';
import {
  extractDateLiterals,
  extractFields,
  extractNumbers,
  findConnectors,
  hasComparator,
  hasKeyword,
} from './lexer.js';

/**
 * Intent detection result
 */
export interface IntentMatch {
  intent: NLIntent;
  confidence: number;
  indicators: string[];
}

/**
 * Detect intent from tokens
 */
export function detectIntent(tokens: NLToken[], input: string): IntentMatch[] {
  const matches: IntentMatch[] = [];

  matches.push(detectComparison(tokens, input));
  matches.push(detectPresence(tokens, input));
  matches.push(detectRange(tokens, input));
  matches.push(detectTextMatch(tokens, input));
  matches.push(detectLength(tokens, input));
  matches.push(detectBoolean(tokens, input));
  matches.push(detectAbsoluteDateComparison(tokens, input));
  matches.push(detectDateComparison(tokens, input));
  matches.push(detectDateArithmetic(tokens, input));
  matches.push(detectConditional(tokens, input));
  matches.push(detectFieldEquality(tokens, input));
  matches.push(detectCombined(tokens, input));

  return matches
    .filter((m) => m.confidence > 0)
    .sort((a, b) => b.confidence - a.confidence);
}

/**
 * Detect comparison intent: "age is at least 18"
 */
function detectComparison(tokens: NLToken[], _input: string): IntentMatch {
  const indicators: string[] = [];
  let confidence = 0;

  const comparators = [
    'at least',
    'greater than',
    'less than',
    'at most',
    'more than',
    'no more than',
    'minimum',
    'maximum',
    'over',
    'not equal to',
    'equals',
    'equal',
    'above',
    'below',
    'under',
  ];

  for (const comp of comparators) {
    if (hasComparator(tokens, comp) || hasKeyword(tokens, comp)) {
      indicators.push(comp);
      confidence += 0.4;
    }
  }

  if (
    tokens.some(
      (token) =>
        token.type === 'operator' &&
        ['>', '>=', '<', '<=', '==', '!=', '='].includes(token.value),
    )
  ) {
    confidence += 0.5;
    indicators.push('symbolic comparator');
  }

  const fields = extractFields(tokens);
  const numbers = extractNumbers(tokens);

  if (fields.length >= 1 && numbers.length >= 1) {
    confidence += 0.3;
    indicators.push('field+number pattern');
  }

  if (hasKeyword(tokens, 'is') || hasKeyword(tokens, 'must')) {
    confidence += 0.1;
  }

  if (hasKeyword(tokens, 'between') || hasKeyword(tokens, 'and', 0.9)) {
    confidence -= 0.2;
  }

  return {
    intent: 'comparison',
    confidence: Math.max(0, Math.min(1, confidence)),
    indicators,
  };
}

function detectLength(tokens: NLToken[], _input: string): IntentMatch {
  const hasLengthWord = ['character', 'characters', 'length', 'long'].some(
    (word) => hasKeyword(tokens, word),
  );
  const fields = extractFields(tokens);
  const numbers = extractNumbers(tokens);
  if (!hasLengthWord || fields.length === 0 || numbers.length === 0) {
    return { intent: 'length', confidence: 0, indicators: [] };
  }
  return {
    intent: 'length',
    confidence: 0.95,
    indicators: ['length word', 'field+number'],
  };
}

/**
 * Detect presence intent: "email is present"
 */
function detectPresence(tokens: NLToken[], _input: string): IntentMatch {
  const indicators: string[] = [];
  let confidence = 0;

  if (hasKeyword(tokens, 'present')) {
    indicators.push('present');
    confidence += 0.5;
  }
  if (hasKeyword(tokens, 'empty')) {
    indicators.push('empty');
    confidence += 0.5;
  }
  if (hasKeyword(tokens, 'blank') || hasKeyword(tokens, 'missing')) {
    indicators.push('blank/missing');
    confidence += 0.5;
  }
  if (hasKeyword(tokens, 'provided')) {
    indicators.push('provided');
    confidence += 0.4;
  }

  const fields = extractFields(tokens);
  const numbers = extractNumbers(tokens);

  if (fields.length >= 1 && numbers.length === 0) {
    confidence += 0.2;
  }

  if (hasKeyword(tokens, 'has') && hasKeyword(tokens, 'text')) {
    indicators.push('has text');
    confidence += 0.3;
  }

  return {
    intent: 'presence',
    confidence: Math.max(0, Math.min(1, confidence)),
    indicators,
  };
}

/**
 * Detect range intent: "age is between 18 and 65"
 */
function detectRange(tokens: NLToken[], _input: string): IntentMatch {
  const indicators: string[] = [];
  let confidence = 0;

  if (hasKeyword(tokens, 'between')) {
    indicators.push('between');
    confidence += 0.6;
  }
  if (hasKeyword(tokens, 'within')) {
    indicators.push('within');
    confidence += 0.4;
  }
  if (hasKeyword(tokens, 'outside')) {
    indicators.push('outside');
    confidence += 0.5;
  }

  const fields = extractFields(tokens);
  const numbers = extractNumbers(tokens);

  if (fields.length >= 1 && numbers.length >= 2) {
    confidence += 0.3;
    indicators.push('field+2numbers');
  } else if (fields.length >= 3) {
    confidence += 0.4;
    indicators.push('field+2fields');
  }

  const connectors = findConnectors(tokens);
  if (connectors.some((c) => c.type === 'and')) {
    if (hasKeyword(tokens, 'between') || hasKeyword(tokens, 'outside')) {
      confidence += 0.2;
      indicators.push('between+and');
    } else if (numbers.length >= 2) {
      confidence += 0.1;
    }
  }

  return {
    intent: 'range',
    confidence: Math.max(0, Math.min(1, confidence)),
    indicators,
  };
}

/**
 * Detect text match intent: "name contains 'test'"
 */
function detectTextMatch(tokens: NLToken[], _input: string): IntentMatch {
  const indicators: string[] = [];
  let confidence = 0;

  if (hasKeyword(tokens, 'contains') || hasKeyword(tokens, 'contain')) {
    indicators.push('contains');
    confidence += 0.6;
  }
  if (hasKeyword(tokens, 'starts')) {
    indicators.push('starts');
    confidence += 0.5;
  }
  if (hasKeyword(tokens, 'ends')) {
    indicators.push('ends');
    confidence += 0.5;
  }
  if (hasKeyword(tokens, 'matches')) {
    indicators.push('matches');
    confidence += 0.4;
  }

  if (hasKeyword(tokens, 'equals') || hasKeyword(tokens, 'equal')) {
    const hasString = tokens.some((t) => t.type === 'string');
    if (hasString) {
      indicators.push('equals with string');
      confidence += 0.6;
    }
  }

  const hasString = tokens.some((t) => t.type === 'string');
  if (hasString) {
    confidence += 0.2;
    indicators.push('string literal');
  }

  if (hasKeyword(tokens, 'has') && hasKeyword(tokens, 'text')) {
    confidence += 0.3;
    indicators.push('has text');
  }

  return {
    intent: 'text-match',
    confidence: Math.max(0, Math.min(1, confidence)),
    indicators,
  };
}

/**
 * Detect boolean intent: "disclaimer is checked"
 */
function detectBoolean(tokens: NLToken[], _input: string): IntentMatch {
  const indicators: string[] = [];
  let confidence = 0;

  if (hasKeyword(tokens, 'checked')) {
    indicators.push('checked');
    confidence += 0.7;
  }
  if (hasKeyword(tokens, 'unchecked')) {
    indicators.push('unchecked');
    confidence += 0.7;
  }
  if (hasKeyword(tokens, 'ticked') || hasKeyword(tokens, 'selected')) {
    indicators.push('boolean synonym');
    confidence += 0.5;
  }

  return {
    intent: 'boolean',
    confidence: Math.max(0, Math.min(1, confidence)),
    indicators,
  };
}

/**
 * Detect date comparison: "discharge is after admission"
 */
function detectDateComparison(tokens: NLToken[], _input: string): IntentMatch {
  const indicators: string[] = [];
  let confidence = 0;

  if (hasKeyword(tokens, 'after')) {
    indicators.push('after');
    confidence += 0.4;
  }
  if (hasKeyword(tokens, 'before')) {
    indicators.push('before');
    confidence += 0.4;
  }

  const fields = extractFields(tokens);
  const dateFields = fields.filter((f) =>
    /date|time|discharge|admission|start|end|arrival|departure/i.test(f),
  );

  if (dateFields.length >= 2) {
    confidence += 0.3;
    indicators.push('date fields');
  }

  if (hasKeyword(tokens, 'date') && hasKeyword(tokens, 'only')) {
    confidence += 0.2;
    indicators.push('date only');
  }

  return {
    intent: 'date-comparison',
    confidence: Math.max(0, Math.min(1, confidence)),
    indicators,
  };
}

/**
 * Detect date arithmetic: "discharge at least 3 days after admission" OR "incident not more than 30 days ago"
 */
function detectDateArithmetic(tokens: NLToken[], _input: string): IntentMatch {
  const indicators: string[] = [];
  let confidence = 0;

  const hasTimeUnit =
    hasKeyword(tokens, 'days') ||
    hasKeyword(tokens, 'day') ||
    hasKeyword(tokens, 'hours') ||
    hasKeyword(tokens, 'hour') ||
    hasKeyword(tokens, 'minutes') ||
    hasKeyword(tokens, 'minute');

  if (hasTimeUnit) {
    indicators.push('time unit');
    confidence += 0.5;
  }

  if (hasKeyword(tokens, 'within')) {
    indicators.push('within');
    confidence += 0.2;
  }
  if (hasKeyword(tokens, 'exactly')) {
    indicators.push('exactly');
    confidence += 0.2;
  }

  if (hasKeyword(tokens, 'after') || hasKeyword(tokens, 'before')) {
    indicators.push('date relation');
    confidence += 0.3;
  }

  if (hasKeyword(tokens, 'ago')) {
    indicators.push('ago pattern');
    confidence += 0.4;
  }

  if (hasKeyword(tokens, 'within') && hasKeyword(tokens, 'last')) {
    indicators.push('within last pattern');
    confidence += 0.4;
  }

  const numbers = extractNumbers(tokens);
  const fields = extractFields(tokens);

  const hasLastKeyword = hasKeyword(tokens, 'last');
  const minFields =
    hasKeyword(tokens, 'ago') ||
    (hasKeyword(tokens, 'within') && hasLastKeyword)
      ? 1
      : 2;

  if (numbers.length >= 1 && hasTimeUnit && fields.length >= minFields) {
    confidence += 0.2;
    indicators.push('complete pattern');
  }

  return {
    intent: 'date-arithmetic',
    confidence: Math.max(0, Math.min(1, confidence)),
    indicators,
  };
}

/**
 * Detect conditional: "if age >= 18 then consent required"
 */
function detectConditional(tokens: NLToken[], _input: string): IntentMatch {
  const indicators: string[] = [];
  let confidence = 0;

  if (hasKeyword(tokens, 'if')) {
    indicators.push('if');
    confidence += 0.5;
  }
  if (hasKeyword(tokens, 'then')) {
    indicators.push('then');
    confidence += 0.3;
  }
  if (hasKeyword(tokens, 'unless')) {
    indicators.push('unless');
    confidence += 0.6;
  }
  if (hasKeyword(tokens, 'required')) {
    indicators.push('required');
    confidence += 0.2;
  }
  if (hasKeyword(tokens, 'required')) {
    indicators.push('required');
    confidence += 0.2;
  }

  return {
    intent: 'conditional',
    confidence: Math.max(0, Math.min(1, confidence)),
    indicators,
  };
}

/**
 * Detect field equality: "email equals confirm_email"
 */
function detectFieldEquality(tokens: NLToken[], _input: string): IntentMatch {
  const indicators: string[] = [];
  let confidence = 0;
  let hasEqualityKeyword = false;

  const hasString = tokens.some((t) => t.type === 'string');
  if (hasString) {
    return {
      intent: 'field-equality',
      confidence: 0,
      indicators: ['has string literal - not field equality'],
    };
  }

  const fields = extractFields(tokens);
  const numbers = extractNumbers(tokens);

  if (fields.length >= 2 && numbers.length === 0) {
    confidence += 0.3;
    indicators.push('2+ fields');
  }

  if (hasKeyword(tokens, 'equals') || hasKeyword(tokens, 'equal')) {
    indicators.push('equals');
    confidence += 0.3;
    hasEqualityKeyword = true;
  }

  if (hasComparator(tokens, 'same as') || hasKeyword(tokens, 'same')) {
    indicators.push('same');
    confidence += 0.4;
    hasEqualityKeyword = true;
  }

  if (hasComparator(tokens, 'different from')) {
    indicators.push('different');
    confidence += 0.4;
    hasEqualityKeyword = true;
  }
  if (hasKeyword(tokens, 'matches') || hasKeyword(tokens, 'match')) {
    indicators.push('matches');
    confidence += 0.2;
    hasEqualityKeyword = true;
  }

  if (!hasEqualityKeyword) {
    confidence = 0;
  }

  return {
    intent: 'field-equality',
    confidence: Math.max(0, Math.min(1, confidence)),
    indicators,
  };
}

/**
 * Detect combined conditions: "age > 18 and disclaimer is checked"
 */
function detectCombined(tokens: NLToken[], _input: string): IntentMatch {
  const indicators: string[] = [];
  let confidence = 0;

  const connectors = findConnectors(tokens);

  if (connectors.length > 0) {
    indicators.push(`${connectors.length} connectors`);

    confidence += 0.8 * connectors.length;
  }

  const fields = extractFields(tokens);
  if (fields.length >= 2) {
    confidence += 0.1;
    indicators.push('multiple fields');
  }

  return {
    intent: 'combined',
    confidence: Math.max(0, Math.min(1, confidence)),
    indicators,
  };
}

/**
 * Detect absolute date comparison: "root.something is greater than or equal to the 5th of April 2022"
 */
function detectAbsoluteDateComparison(
  tokens: NLToken[],
  _input: string,
): IntentMatch {
  const indicators: string[] = [];
  let confidence = 0;

  // Must have a date-literal token
  const dateLiterals = extractDateLiterals(tokens);
  if (dateLiterals.length === 0) {
    return { intent: 'absolute-date-comparison', confidence: 0, indicators };
  }

  indicators.push('date-literal');
  confidence += 0.6;

  // Must have a field
  const fields = extractFields(tokens);
  if (fields.length >= 1) {
    confidence += 0.2;
    indicators.push('field present');
  }

  // Boost for explicit comparison keywords
  const dateComparators = [
    'greater than or equal to',
    'less than or equal to',
    'greater than',
    'less than',
    'at least',
    'at most',
    'on or after',
    'on or before',
    'same or after',
    'same or before',
  ];
  for (const comp of dateComparators) {
    if (hasComparator(tokens, comp)) {
      indicators.push(comp);
      confidence += 0.2;
      break;
    }
  }

  if (hasKeyword(tokens, 'after') || hasKeyword(tokens, 'before')) {
    indicators.push('after/before');
    confidence += 0.1;
  }

  return {
    intent: 'absolute-date-comparison',
    confidence: Math.max(0, Math.min(1, confidence)),
    indicators,
  };
}

/**
 * Get the best intent match
 */
export function getBestIntent(tokens: NLToken[], input: string): IntentMatch {
  const matches = detectIntent(tokens, input);
  return matches[0] || { intent: 'unknown', confidence: 0, indicators: [] };
}

/**
 * Get all possible intents above threshold
 */
export function getPossibleIntents(
  tokens: NLToken[],
  input: string,
  threshold = 0.3,
): IntentMatch[] {
  return detectIntent(tokens, input).filter((m) => m.confidence >= threshold);
}
